<template>
  <multiselect
    ref="multiselectElement"
    :options="searchResults"
    :multiple="true"
    :loading="searchLoading"
    track-by="vesselName"
    label="vesselName"
    selected-label=""
    selectLabel=""
    :close-on-select="true"
    :internal-search="false"
    @search-change="searchChangeHandler"
    @remove="handleSearchResultRemoved"
    @select="handleSearchResultSelected"
    class="search-multiselect"
    placeholder="Vessel Search"
  >
    <template #beforeList>
      <div class="dropdown-title">Vessels</div>
    </template>

    <template #option="{option}">
      <table style="width:100%">
        <tr>
          <th colspan="4">
            {{ getNested(option, "name", "--") }}
            <small> {{ computeExName(option) }}</small>
          </th>
        </tr>
        <tr>
          <td width="23%">
            <small
              >IMO: <strong>{{ getNested(option, "imo", "--") }}</strong></small
            >
          </td>
          <td width="17%">
            <small
              >Dwt: <strong>{{ getNested(option, "dwt", "--") }}</strong></small
            >
          </td>
          <td width="42%">
            <small
              >Type:
              <strong>{{
                getNested(option, "highlevelType", "--")
              }}</strong></small
            >
          </td>
          <td width="18%">
            <small
              >Built:
              <strong>{{ getNested(option, "built", "--") }}</strong></small
            >
          </td>
        </tr>
      </table>
    </template>
  </multiselect>
</template>

<script>
import getNested from "lodash-es/get";
import debounce from "lodash-es/debounce";
import Multiselect from "vue-multiselect";
import { mapActions, mapGetters } from "vuex";

const debounceDelay = 300;

export default {
  name: "vessel-search",

  components: {
    Multiselect
  },

  data() {
    return {
      searchIndex: "vessels_prod",
      apiRequestIsPerforming: false,
      searchLoading: false,
      searchResults: [],
      selectedValue: null
    };
  },

  methods: {
    getNested: getNested,

    ...mapActions("api", ["algoliaSearch"]),

    searchChangeHandler: debounce(function(searchString) {
      this.searchChange(searchString);
    }, debounceDelay),

    async searchChange(searchString) {
      this.searchLoading = true;
      if (searchString) {
        let response;

        if (this.isIMOString(searchString)) {
          // from algolia docs:
          // "An empty query can be used to fetch all records"
          response = await this.algoliaSearch({
            searchIndex: this.searchIndex,
            query: "",
            searchParameters: {filters: `imo = ${searchString}`},
          });
        } else {
          response = await this.algoliaSearch({
            searchIndex: this.searchIndex,
            query: searchString
          });
        }

        this.searchResults = getNested(response, "hits", []).map(item => {
          let id = `${item.imo}`;
          item.id = id;
          item.value = id;
          item.text = item.imo;
          return item;
        });
      }
      this.searchLoading = false;
    },

    isIMOString(searchString) {
      // is an exact IMO string
      return !!searchString.match(/^\d{7}$/);
    },

    handleSearchResultSelected(selectedValue) {
      // @select is used since
      // we need to have an updated "this.searchValue" and selectedValue at the same time

      this.selectedValue = selectedValue;
      this.$nextTick(() => {
        this.$emit("input", selectedValue);
      });
    },

    handleSearchResultRemoved() {
      this.selectedValue = null;
      this.$nextTick(() => {
        this.$emit("input", null);
      });
    },

    computeExName(vessel) {
      if (vessel.exName) {
        return "(Ex name: " + vessel.exName + ")";
      } else {
        return "";
      }
    }
  },

  computed: {
    ...mapGetters("api", ["getActiveRequestsCount"]),

    routeVesselIMO() {
      return getNested(this, "$route.query.imo", null);
    }
  },

  watch: {
    routeVesselIMO: {
      immediate: true,
      handler: async function(vesselIMO) {
        // console.log('watch route', vesselIMO, typeof vesselIMO);

        if (vesselIMO) {
          let currentVesselIMO = getNested(this, "selectedValue.imo", null);
          if (`${currentVesselIMO}` === vesselIMO) {
            // prevent double loading when vessel is changed by search widget
            return;
          }

          await this.searchChange(`${vesselIMO}`);

          const vessel = this.searchResults.find(
            item => `${item.imo}` === `${vesselIMO}`
          );

          if (vessel) {
            this.$emit("input", vessel);
          } else {
            this.$refs.multiselectElement.$el.focus();
          }
        }
      }
    },

    getActiveRequestsCount: debounce(function(newValue) {
      this.apiRequestIsPerforming = !!newValue;
    }, debounceDelay)
  }
};
</script>

<style scoped>
.search-multiselect.multiselect--active {
  min-width: 600px !important;
  transition: all 0.2s;
}

.search-multiselect.multiselect--active >>> .multiselect__input {
  min-width: 100%;
}

.search-multiselect >>> .multiselect__input {
  background-color: transparent;
}

.search-multiselect >>> .multiselect__select {
  background-color: transparent;
}
</style>

<style scoped lang="scss">
.search-multiselect {
  width: 300px;
}
</style>
